import classNames from "classnames"
import { Tooltip } from "components/Tooltip/Tooltip"
import { ReactNode } from "react"

/** Anchor id linking truncating cells to the single shared tooltip instance. */
export const SHARED_ELLIPSIS_TOOLTIP_ID = "shared-ellipsis-tooltip"

/**
 * Whether the element (or a descendant, e.g. a truncating span inside a badge)
 * currently overflows. Checked at hover time, so anchors need no measuring
 * effects or resize listeners of their own.
 */
const isTruncated = (element: HTMLElement): boolean => {
  if (element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight) {
    return true
  }
  for (const child of element.querySelectorAll<HTMLElement>("*")) {
    if (child.offsetWidth < child.scrollWidth) {
      return true
    }
  }
  return false
}

interface EllipsisAnchorProps {
  children: ReactNode
  className?: string
  /**
   * Tooltip text override. Defaults to the anchor's text content; pass this
   * when that concatenates poorly (e.g. multiple names — join with "\n").
   */
  content?: string
}

/**
 * A single-line truncating container whose full text appears in the shared
 * tooltip when it overflows. Renders no tooltip machinery itself — the one
 * {@link SharedEllipsisTooltip} instance mounted by `AppWrapper` serves every
 * anchor, which is what keeps large tables cheap.
 */
export const EllipsisAnchor = ({ children, className, content }: EllipsisAnchorProps) => (
  <div
    className={classNames("truncate", className)}
    data-tooltip-id={SHARED_ELLIPSIS_TOOLTIP_ID}
    data-tooltip-content={content}
  >
    {children}
  </div>
)

/**
 * The app-wide tooltip serving every {@link EllipsisAnchor}: on hover it shows
 * the anchor's full text, and only when the content is actually truncated.
 *
 * Mounted ONCE by `AppWrapper`. Never mount a react-tooltip instance per table
 * cell (the old `EllipsisWithTooltip`-per-cell pattern): every instance
 * attaches its own document-wide MutationObserver and renders its content a
 * second time, which froze large tables like the 200-row inbox.
 */
export const SharedEllipsisTooltip = () => (
  <Tooltip
    id={SHARED_ELLIPSIS_TOOLTIP_ID}
    place="top"
    positionStrategy="fixed"
    className="!z-50 !rounded-md !bg-gray-900 !px-4 !py-2"
    render={({ content, activeAnchor }) =>
      activeAnchor && isTruncated(activeAnchor) ? (
        <span className="whitespace-pre-line !text-body3/regular !text-white [&_a]:!text-white">
          {/* innerText (not textContent): keeps line breaks between the anchor's text blocks */}
          {content ?? activeAnchor.innerText}
        </span>
      ) : null
    }
  />
)
